import React, { useState, useCallback, useMemo, forwardRef } from 'react';
import t from '@guestyci/localize/t.macro';
import {
  Stack,
  Input,
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxSearchInput,
  ComboboxList,
  ComboboxItem,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@guestyci/arc';
import { COUNTRIES, DEFAULT_COUNTRY_ISO, getCountryByIso } from './countryData';

const CountryFlag = ({ country, className = 'gst-w-5 gst-h-4' }) => {
  const Flag = country.FlagComponent;
  return Flag ? (
    <Flag className={className} aria-hidden="true" />
  ) : (
    <span aria-hidden="true">{country.iso}</span>
  );
};
CountryFlag.displayName = 'CountryFlag';

const CountryOption = ({ country }) => (
  <TooltipProvider delayDuration={400}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Stack direction="row" spacing={2} align="center" className="gst-w-full gst-min-w-0">
          <span className="gst-shrink-0"><CountryFlag country={country} /></span>
          <span className="gst-flex-1 gst-min-w-0 gst-truncate">{country.name}</span>
          <span className="gst-shrink-0 gst-text-muted-foreground">{country.dialCode}</span>
        </Stack>
      </TooltipTrigger>
      <TooltipContent side="right">
        {t('{name} ({dialCode})', { name: country.name, dialCode: country.dialCode })}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
CountryOption.displayName = 'CountryOption';

const CountryTriggerContent = ({ country }) => (
  <Stack direction="row" spacing={1} align="center" className="gst-shrink-0">
    <CountryFlag country={country} />
    <span>{country.dialCode}</span>
  </Stack>
);
CountryTriggerContent.displayName = 'CountryTriggerContent';

const PhoneInput = forwardRef(({
  value = '',
  onChange,
  countryCode = DEFAULT_COUNTRY_ISO,
  onCountryChange,
  defaultCountry,
  disabled = false,
  placeholder,
  className,
  ...rest
}, ref) => {
  const [searchValue, setSearchValue] = useState('');

  const resolvedCountryCode = countryCode || defaultCountry || DEFAULT_COUNTRY_ISO;
  const selectedCountry = useMemo(
    () => getCountryByIso(resolvedCountryCode) || getCountryByIso(DEFAULT_COUNTRY_ISO),
    [resolvedCountryCode]
  );

  const filteredCountries = useMemo(() => {
    if (!searchValue) return COUNTRIES;
    const query = searchValue.toLowerCase();
    return COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(query) ||
      c.dialCode.includes(query) ||
      c.iso.toLowerCase().includes(query));
  }, [searchValue]);

  const handleCountryChange = useCallback(
    (country) => {
      if (country && onCountryChange) {
        onCountryChange(country.iso);
      }
    },
    [onCountryChange]
  );

  const handlePhoneChange = useCallback(
    (e) => {
      if (onChange) {
        onChange(e.target.value.replace(/[^0-9]/g, ''));
      }
    },
    [onChange]
  );

  const handleSearchChange = useCallback((val) => {
    setSearchValue(val);
  }, []);

  return (
    <Stack
      ref={ref}
      direction="row"
      spacing={0}
      className={className}
      data-slot="phone-input"
      {...rest}
    >
      <Combobox
        value={selectedCountry}
        onValueChange={handleCountryChange}
        itemKey="iso"
        disabled={disabled}
      >
        <ComboboxTrigger
          className="gst-shrink-0 gst-w-auto gst-rounded-r-none"
          renderTrigger={(country) =>
            country ? (
              <CountryTriggerContent country={country} />
            ) : null
          }
        />
        <ComboboxContent className="gst-min-w-72">
          <ComboboxSearchInput
            placeholder={t('Search country...')}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
          />
          <ComboboxList virtualThreshold={100}>
            {filteredCountries.map((country) => (
              <ComboboxItem
                key={country.iso}
                value={country}
              >
                <CountryOption country={country} />
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      <Input
        type="tel"
        inputMode="numeric"
        value={value}
        onChange={handlePhoneChange}
        placeholder={placeholder || t('Enter phone number')}
        disabled={disabled}
        className="gst-flex-1 gst-rounded-l-none gst-border-l-0"
      />
    </Stack>
  );
});

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
